import { createFileRoute, Navigate, Outlet, useChildMatches } from '@tanstack/react-router'
import { useKanbanCards } from '~/components/routes/development/real-estate/kanban/kanban-cards-context'
import { StoreProjectModal } from '~/components/routes/development/real-estate/store-project/store-project-modal'
import type { TabValue } from '~/components/routes/development/real-estate/store-project/store-project-modal/use-store-project-modal'
import { useRealEstatePermissions } from '~/components/routes/development/real-estate/use-real-estate-permissions'

/** Route dédiée de chaque sous-onglet du modal — leur rendu passe par l'`Outlet` du modal. */
const CARD_TAB_ROUTES = {
  general: '/development/real-estate/kanban/$cardId/general',
  analytics: '/development/real-estate/kanban/$cardId/analytics',
  'land-commitment': '/development/real-estate/kanban/$cardId/land-commitment',
  works: '/development/real-estate/kanban/$cardId/works',
  'admin-authorizations': '/development/real-estate/kanban/$cardId/admin-authorizations',
  documents: '/development/real-estate/kanban/$cardId/documents',
} as const satisfies Record<TabValue, string>

export const Route = createFileRoute('/_authenticated/development/real-estate/kanban/$cardId')({
  component: KanbanCardModalRoute,
})

function KanbanCardModalRoute() {
  const { cardId } = Route.useParams()
  const navigate = Route.useNavigate()
  const { projects, isLoading, saveProject, toggleAbandoned } = useKanbanCards()
  const realEstatePermissions = useRealEstatePermissions()

  // L'onglet actif est la route enfant courante (`.../$cardId/general`, `.../documents`, …).
  const childMatches = useChildMatches()
  const lastSegment = childMatches.at(-1)?.routeId.split('/').at(-1)
  const activeTab: TabValue =
    lastSegment && lastSegment in CARD_TAB_ROUTES ? (lastSegment as TabValue) : 'general'

  const project = projects.find((candidate) => candidate.id === cardId)

  if (!project) {
    // Id inconnu ou projet supprimé pendant l'édition : retour au board.
    return isLoading ? null : <Navigate to="/development/real-estate/kanban" replace />
  }

  const closeCard = (): void => {
    void navigate({ to: '/development/real-estate/kanban', search: (prev) => ({ ...prev }) })
  }

  return (
    <StoreProjectModal
      open
      onClose={closeCard}
      onSave={async (projectData, queuedFiles) => {
        const saved = await saveProject(project, projectData, queuedFiles)
        if (saved) closeCard()
      }}
      project={project}
      canEdit={realEstatePermissions.canManageProject(project)}
      canChangeStatusTo={(status) => realEstatePermissions.canMoveProjectTo(project, status)}
      onToggleAbandoned={async (projectId, nextValue) => {
        const done = await toggleAbandoned(projectId, nextValue)
        if (done) closeCard()
      }}
      activeTab={activeTab}
      onTabChange={(tab) => {
        void navigate({
          to: CARD_TAB_ROUTES[tab],
          params: { cardId },
          search: (prev) => ({ ...prev }),
        })
      }}
    >
      <Outlet />
    </StoreProjectModal>
  )
}
